import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Headphones, 
  Send, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Phone,
  Mail,
  MessageSquare
} from 'lucide-react';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

export default function PublicSupportPage() {
  const { companySlug } = useParams();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (companySlug) {
      axios.get(`${API_URL}/api/public/company/${companySlug}`)
        .then(res => setCompanyInfo(res.data))
        .catch(() => setError('Azienda non trovata'));
    }
  }, [companySlug]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = { ...formData };
      if (companySlug) payload.company_slug = companySlug;
      const response = await axios.post(`${API_URL}/api/public/ticket`, payload);
      setSuccess(response.data);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        description: '',
        category: 'general',
        priority: 'medium'
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Si è verificato un errore. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Header */}
      <header className="bg-[#002FA7] text-white">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4">
              <Headphones className="w-8 h-8" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {companyInfo ? `${companyInfo.name} - Supporto` : 'Supporto Tecnico'}
            </h1>
            <p className="text-blue-100 mt-2 max-w-xl mx-auto">
              Hai bisogno di assistenza? Compila il modulo e il nostro team ti contatterà al più presto.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 -mt-8">
        {/* Success Message */}
        {success && (
          <Card className="mb-6 border-green-200 bg-green-50 animate-fade-up" data-testid="success-message">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium text-green-800 text-lg">Richiesta Inviata!</h3>
                  <p className="text-green-700 mt-1">
                    La tua richiesta è stata ricevuta con successo. 
                    Il numero del tuo ticket è: <strong>#{success.ticket_id?.slice(-8)}</strong>
                  </p>
                  <p className="text-green-600 text-sm mt-2">
                    Riceverai una email di conferma all'indirizzo fornito.
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4 border-green-300 text-green-700 hover:bg-green-100"
                    onClick={() => setSuccess(null)}
                  >
                    Invia un'altra richiesta
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form Card */}
        {!success && (
          <Card className="border border-[#E4E4E7] shadow-lg animate-fade-up" data-testid="support-form-card">
            <CardHeader>
              <CardTitle className="text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Richiedi Assistenza
              </CardTitle>
              <CardDescription>
                Descrivi il tuo problema e ti risponderemo il prima possibile
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md" data-testid="form-error">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                {/* Contact Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome Completo *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Mario Rossi"
                      data-testid="input-name"
                      className="border-[#E4E4E7]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="mario@esempio.com"
                      data-testid="input-email"
                      className="border-[#E4E4E7]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefono (opzionale)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+39 123 456 7890"
                      data-testid="input-phone"
                      className="border-[#E4E4E7]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Categoria</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="select-category" className="border-[#E4E4E7]">
                        <SelectValue placeholder="Seleziona categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">Generale</SelectItem>
                        <SelectItem value="hardware">Hardware</SelectItem>
                        <SelectItem value="software">Software</SelectItem>
                        <SelectItem value="network">Rete / Connettività</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Priority */}
                <div className="space-y-2">
                  <Label htmlFor="priority">Priorità</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger data-testid="select-priority" className="border-[#E4E4E7]">
                      <SelectValue placeholder="Seleziona priorità" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Bassa</SelectItem>
                      <SelectItem value="medium">Media</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Subject */}
                <div className="space-y-2">
                  <Label htmlFor="subject">Oggetto *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Breve descrizione del problema"
                    data-testid="input-subject"
                    className="border-[#E4E4E7]"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Descrizione del Problema *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Descrivi dettagliatamente il problema che stai riscontrando..."
                    data-testid="input-description"
                    className="border-[#E4E4E7]"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 h-12 text-base text-white"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Invia Richiesta
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Contact Options */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Card className="border border-[#E4E4E7] text-center" data-testid="contact-option-voice">
            <CardContent className="p-6">
              <Phone className="w-8 h-8 mx-auto text-[#002FA7] mb-3" />
              <h3 className="font-medium">Chiamata Vocale</h3>
              <p className="text-sm text-[#71717A] mt-1">Parla con REBECCA</p>
              <Link to="/voice-call">
                <Button variant="outline" size="sm" className="mt-3">
                  Chiama ora
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border border-[#E4E4E7] text-center" data-testid="contact-option-email">
            <CardContent className="p-6">
              <Mail className="w-8 h-8 mx-auto text-[#002FA7] mb-3" />
              <h3 className="font-medium">Email</h3>
              <p className="text-sm text-[#71717A] mt-1">Scrivi al supporto</p>
              <p className="text-sm text-[#002FA7] font-medium mt-3 break-all">
                {companyInfo ? `${companyInfo.slug}@ikonetsolutions.com` : 'supporto@ikonetsolutions.com'}
              </p>
            </CardContent>
          </Card>

          <Card className="border border-[#E4E4E7] text-center" data-testid="contact-option-chat">
            <CardContent className="p-6">
              <MessageSquare className="w-8 h-8 mx-auto text-[#002FA7] mb-3" />
              <h3 className="font-medium">REBECCA</h3>
              <p className="text-sm text-[#71717A] mt-1">Assistente virtuale 24/7</p>
              <Link to="/ai-chat">
                <Button variant="outline" size="sm" className="mt-3">
                  Avvia chat
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border border-[#0088cc]/30 bg-gradient-to-br from-[#e9f5fb] to-white text-center" data-testid="contact-option-telegram">
            <CardContent className="p-6">
              <div className="w-8 h-8 mx-auto mb-3 flex items-center justify-center rounded-full bg-[#0088cc] text-white">
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
                </svg>
              </div>
              <h3 className="font-medium">Telegram</h3>
              <p className="text-sm text-[#71717A] mt-1">Bot ufficiale IKONET</p>
              <a
                href="https://t.me/ikonet_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-3"
                data-testid="telegram-bot-link"
              >
                <img
                  src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https%3A%2F%2Ft.me%2Fikonet_bot&margin=1"
                  alt="QR Code Telegram @ikonet_bot"
                  className="mx-auto rounded-md border border-[#E4E4E7]"
                  width="120"
                  height="120"
                  data-testid="telegram-qr-code"
                />
                <span className="block mt-2 text-xs text-[#0088cc] font-medium">@ikonet_bot</span>
              </a>
            </CardContent>
          </Card>
        </div>

        {/* Login Link */}
        <div className="text-center mt-8">
          <p className="text-sm text-[#71717A]">
            Sei un tecnico? {' '}
            <Link to="/login" className="text-[#002FA7] hover:underline font-medium">
              Accedi alla dashboard
            </Link>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E4E4E7] mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <p className="text-sm text-[#71717A]">
            IT Helpdesk AI Platform • Assistenza Tecnica Intelligente
          </p>
        </div>
      </footer>
    </div>
  );
}
